import { useState, useEffect } from "react";
import axios from "axios";
import { DateField, type DataTableColumn } from "@routes-plan/ui";
import { Office } from "../../models/office";
import { Route } from "../../models/route";
import { LoanReport } from "../../models/loan-report";
import { minYmd, nowOperationalDateString } from "../../utils/operationalDateTime";
import { useT } from "../../i18n";
import { formatCurrency } from "../../utils/format/currency";
import {
  OfficeSelect,
  ReportEmpty,
  ReportFilters,
  ReportPage,
  ReportResults,
  ReportTable,
  RouteMultiSelect,
} from "./components/ReportLayout";

interface LateLoanRow {
  id: number | string;
  idNumber: string;
  fullName: string;
  loanValue: number;
  value: number;
  paymentDate: string;
  balance: number;
  daysLate: number;
  route: string;
}

/** Préstamos con días de atraso en un periodo. */
const LateLoans = () => {
  const t = useT();
  const [office_id, setOffice_id] = useState("");
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [start_date, setStart_date] = useState("");
  const [end_date, setEnd_date] = useState("");
  const [fechas, setFechas] = useState<LoanReport[]>([]);
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [queried, setQueried] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const formData = {
      endDate: end_date,
      startDate: start_date,
      routeId: selectedRouteIds,
    };
    const url = "/reports/loans/late";
    setQueried(true);
    setLoading(true);
    try {
      const response = await axios.post(url, formData);
      setFechas(response.data.data ?? []);
    } catch (error: any) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (office_id) {
      (async () => {
        try {
          const { data } = await axios.get(`offices/${office_id}/routes`);
          setOfficeRoutes(Array.isArray(data) ? data : []);
          setSelectedRouteIds([]);
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      })();
    }
  }, [office_id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("offices");
        setOffices(data?.data ?? []);
      } catch (error) {
        console.error("Error fetching offices:", error);
      }
    })();
  }, []);

  const rows: LateLoanRow[] = fechas.map((reportLoan: LoanReport) => ({
    id: reportLoan.id,
    idNumber: reportLoan.loan.client.idNumber,
    fullName: reportLoan.loan.client.fullName,
    loanValue: Number(reportLoan.loan.value ?? 0),
    value: Number(reportLoan.value ?? 0),
    paymentDate: String(reportLoan.paymentDate ?? ""),
    balance: Number(reportLoan.balance ?? 0),
    daysLate: Number(reportLoan.daysLate ?? 0),
    route: reportLoan.loan.route.name,
  }));

  const columns: DataTableColumn<LateLoanRow>[] = [
    { id: "id", header: t("loanReports.loanNumber"), value: (r) => r.id, sortable: true },
    { id: "idNumber", header: t("common.idNumber"), value: (r) => r.idNumber, sortable: true, hideBelow: "sm" },
    { id: "fullName", header: t("loanReports.client"), value: (r) => r.fullName, sortable: true },
    { id: "loanValue", header: t("loanReports.loanValue"), value: (r) => r.loanValue, render: (r) => formatCurrency(r.loanValue), align: "right", sortable: true },
    { id: "value", header: t("loanReports.payments"), value: (r) => r.value, render: (r) => formatCurrency(r.value), align: "right", sortable: true },
    { id: "paymentDate", header: t("common.date"), value: (r) => r.paymentDate, sortable: true },
    { id: "balance", header: t("loanReports.balance"), value: (r) => r.balance, render: (r) => formatCurrency(r.balance), align: "right", sortable: true },
    { id: "daysLate", header: t("loanReports.daysLate"), value: (r) => r.daysLate, align: "right", sortable: true },
    { id: "route", header: t("summaryReports.shared.route"), value: (r) => r.route, sortable: true },
  ];

  return (
    <ReportPage title={t("loanReports.lateLoans.title")} description={t("loanReports.lateLoans.description")}>
      <ReportFilters onSubmit={() => void submit()} submitDisabled={loading}>
        <DateField
          label={t("loanReports.startDate")}
          value={start_date || null}
          onChange={(v) => setStart_date(v ?? "")}
          max={minYmd(end_date || nowOperationalDateString(), nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t("loanReports.endDate")}
          value={end_date || null}
          onChange={(v) => setEnd_date(v ?? "")}
          min={start_date || undefined}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={office_id} onChange={setOffice_id} required />
        <RouteMultiSelect routes={officeRoutes} value={selectedRouteIds} onChange={setSelectedRouteIds} />
      </ReportFilters>

      <ReportResults>
        <ReportTable<LateLoanRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          exportFileName={t("loanReports.lateLoans.exportFile")}
          ariaLabel={t("loanReports.lateLoans.title")}
          initialSort={{ columnId: "daysLate", direction: "desc" }}
          emptyState={<ReportEmpty queried={queried} />}
        />
      </ReportResults>
    </ReportPage>
  );
};

export default LateLoans;
